import { FunctionComponent, ReactElement, ReactNode, SVGProps } from 'react'

import { InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import { darken } from 'polished'
import { Translation, useTranslation } from 'react-i18next'

import {
    formatMarketAnalysisSegmentsFilters,
    getMarketAnalysisSegmentsExport,
    getMarketAnalysisSegmentsWidgetData,
} from 'api/Atlas/marketAnalysisSegmentsWidgetApi'
import {
    getAdGroupFacts,
    getAdGroupFactsExport,
} from 'api/Banner/adGroupFactsApi'
import {
    getAmsStreamFacts,
    getAmsStreamFactsExport,
} from 'api/Banner/amsStreamFactsApi'
import { formatAsinUsageFilterParams } from 'api/Banner/asinUsageFacts/apiParamFormatters'
import {
    getAsinUsageSummary,
    getAsinUsageSummaryExport,
} from 'api/Banner/asinUsageFacts/asinUsageFactsApi'
import {
    getCampaignFacts,
    getCampaignFactsExport,
} from 'api/Banner/campaignFactsApi'
import {
    getCampaignPlacementFacts,
    getCampaignPlacementFactsExport,
} from 'api/Banner/campaignPlacementFactsApi'
import {
    getCategoryPerformanceFacts,
    getCategoryPerformanceFactsExport,
} from 'api/Banner/categoryPerformanceFacts/categoryPerformanceApi'
import {
    getChangelogSummary,
    getChangelogSummaryExport,
} from 'api/Banner/changelogFactsApi'
import {
    getDspAudienceFacts,
    getDspAudienceFactsExport,
} from 'api/Banner/dspAudienceFacts'
import {
    getDspCampaignFacts,
    getDspCampaignFactsExport,
} from 'api/Banner/dspCampaignFacts'
import {
    getDspInventoryFacts,
    getDspInventoryFactsExport,
} from 'api/Banner/dspInventoryFacts'
import {
    getKeywordFacts,
    getKeywordFactsExport,
} from 'api/Banner/keywordFactsApi'
import {
    getKeywordQueryFacts,
    getKeywordQueryFactsExport,
} from 'api/Banner/keywordQueryFactsApi'
import { getSovKeywordsForAsin } from 'api/Banner/keywordResearch/keywordResearchApi'
import { getOrgFacts, getOrgFactsExport } from 'api/Banner/orgFactsApi'
import { getOrgPerformanceFacts } from 'api/Banner/orgPerformanceFactsApi'
import {
    getProductAdFacts,
    getProductAdFactsExport,
} from 'api/Banner/productAdFactsApi'
import {
    getSBPurchasedProductsFacts,
    getSBPurchasedProductsFactsExport,
} from 'api/Banner/sbPurchasedProductsFactsApi'
import { formatSegmentFactsApiFilterParams as formatSegmentFactsFilters } from 'api/Banner/segmentFacts/apiParamFormatters'
import {
    getSegmentFacts,
    getSegmentFactsExport,
    getSegmentVariantLevelFacts,
    getSegmentVariantLevelFactsExport,
} from 'api/Banner/segmentFacts/segmentFactsApi'
import {
    getScOrderV2Metrics,
    getScOrderV2MetricsExport,
    getScSalesAndTrafficData,
    getScSalesAndTrafficDataExport,
    getScSellerAnalyticsData,
    getScSellerAnalyticsDataExport,
} from 'api/Banner/sellerCentralApi'
import {
    getSovIntradayFactAggregates,
    getSovIntradayFactAggregatesExport,
} from 'api/Banner/sovSearchResultsApi'
import { getTargetFacts, getTargetFactsExport } from 'api/Banner/targetFactsApi'
import {
    getTargetQueryFacts,
    getTargetQueryFactsExport,
} from 'api/Banner/targetQueryFactsApi'
import {
    getVcApiInventoryHealthFacts,
    getVcApiInventoryHealthFactsExport,
    getVcApiInventoryManufacturingMetrics,
    getVcApiInventoryManufacturingMetricsExport,
    getVcApiInventorySourcingMetrics,
    getVcApiInventorySourcingMetricsExport,
    getVcApiSalesDiagnosticManufacturerMetrics,
    getVcApiSalesDiagnosticManufacturerMetricsExport,
    getVcApiSalesDiagnosticSourcingMetrics,
    getVcApiSalesDiagnosticSourcingMetricsExport,
    getVcInventoryHealthFactAggregates,
    getVcInventoryHealthFactAggregatesExport,
    getVcManufacturerFactAggregates,
    getVcManufacturerFactAggregatesExport,
    getVcSourceFactAggregates,
    getVcSourceFactAggregatesExport,
    getVcTrafficDiagnosticFactAggregates,
    getVcTrafficDiagnosticFactAggregatesExport,
} from 'api/Banner/vendorCentralApi'
import { Text } from 'components/core'
import { date, type Dayjs } from 'configuration/dayjs'
import { variant_count__total } from 'configuration/fieldCreators/metrics/asinSalesEstimateFacts'
import { metadataId } from 'configuration/fieldCreators/productMetadata'
import { productId } from 'configuration/fieldCreators/segments'
import widgetsTranslation from 'configuration/widgetsTranslation'
import { COUNTRY_CODES, COUNTRY_CURRENCIES, COUNTRY_LABELS } from 'const/codes'
import { WidgetDataSourceKey } from 'const/dataSources'
import {
    AD_ACCOUNT_TYPES,
    AD_GROUP_STATES,
    ADVANCED_AUTOMATIONS,
    ADVANCED_PRODUCT_TITLE,
    ADVANCED_TEXT,
    AUTOMATIONS,
    BRANDS,
    CAMPAIGN_AD_FORMATS,
    CAMPAIGN_STATES,
    CAMPAIGN_TARGETING_TYPES,
    CAMPAIGNS,
    COUNTRIES,
    DATES,
    DSP_ADVERTISERS,
    DSP_AUDIENCE_ID,
    DSP_AUDIENCE_NAME,
    DSP_COUNTRIES,
    DSP_CREATIVE_ID,
    DSP_CREATIVE_NAME,
    DSP_CREATIVE_TYPE,
    DSP_LINE_ITEMS,
    DSP_TARGETING_METHOD,
    DSP_ORDERS,
    DSP_SUPPLY_SOURCE,
    FACT_TYPES,
    KEYWORD_MATCH_TYPES,
    KEYWORD_STATES,
    KEYWORDS,
    LABELS,
    MARKET_ANALYSIS_SEGMENTS,
    METRIC_FILTERS,
    ORGANIZATIONS,
    PORTFOLIOS,
    PRODUCT_AD_STATES,
    PRODUCT_ADS,
    PRODUCT_ASINS,
    PRODUCT_NUMBER_OF_ITEMS,
    RANGE_LAG,
    REGIONS,
    RESOURCE_TYPES,
    SC_ACCOUNTS,
    SC_ASINS,
    SEARCH_TERM,
    SEGMENT_BRANDS,
    SELLER_NAME,
    SOV_ASINS,
    SOV_BRANDS,
    SOV_COUNTRIES,
    SOV_FOLDS,
    SOV_KEYWORDS,
    SOV_LANGUAGES,
    SOV_LAYOUT,
    SOV_METADATA_BRANDS,
    SOV_PLATFORMS,
    SOV_RANK,
    SOV_RESULT_TYPES,
    SOV_STATES,
    TARGET_STATES,
    VC_INTEGRATIONS,
    VC_PROGRAMS,
} from 'const/filters'
import {
    CAMPAIGN_STATES as CAMPAIGN_STATES_OPTIONS,
    ENABLED,
    KEYWORD_STATE_LABELS,
    PRODUCT_AD_STATES as PRODUCT_AD_STATES_OPTIONS,
    STATE_FIELD_OPTIONS,
    TARGET_STATE_LABELS,
} from 'const/resourceStates'
import {
    CATEGORY_CHART,
    MAX_DAYS_RANGE_FOR_HOURLY_DATA,
    METRIC,
    REPORT_DATE_DAY_ID,
    REPORT_DATE_HOUR_ID,
    TABLE,
    TEXT,
    TIME_SERIES,
} from 'const/widgets'
import {
    ADS_LAG_HOURS,
    SELLER_CENTRAL_LAG_HOURS,
    VENDOR_CENTRAL_LAG_HOURS,
} from 'features/FactData/dataSourceLag'
import { SegmentCustomWidgetSettings } from 'features/Segments/components/SegmentDashboardWidgetConfig'
import {
    AMS_CHART_FORMATTERS,
    CATEGORY_PERFORMANCE_FACT_CHART_FORMATTERS,
    CHANGELOG_CHART_FORMATTERS,
    DSP_CHART_FORMATTERS,
    ORG_FACT_CHART_FORMATTERS,
    SC_CHART_FORMATTERS,
    SOV_CHART_FORMATTERS,
    VC_CHART_FORMATTERS,
} from 'helpers/charts'
import {
    calculateCategoryPerformanceLag,
    calculateSegmentLag,
    getPriorPeriodDateRange,
    hoursToWeeklyDataBoundary,
} from 'helpers/dateRange'
import {
    userHasAmazonAdvertisingPermissions,
    userHasCustomerServicePermissions,
    userHasLegacyVendorCentralReadPermissions,
    userHasReadOrManageSegmentPermissions,
    userHasSellerCentralReadPermissions,
    userHasSovReadPermissions,
} from 'helpers/featurePermissions'
import { formatFilters as formatAdGroupFactsFilters } from 'helpers/filters/adGroupFacts'
import { formatFilters as formatCampaignFactsFilters } from 'helpers/filters/campaignFacts'
import { formatFilters as formatCategoryPerformanceFactsFilters } from 'helpers/filters/categoryPerformanceFacts'
import { formatFilters as formatChangelogFilters } from 'helpers/filters/changelog'
import { formatFilters as formatDspFactsFilters } from 'helpers/filters/dspFacts'
import { formatFilters as formatKeywordFactsFilters } from 'helpers/filters/keywordFacts'
import { formatFilters as formatOrgFactsFilters } from 'helpers/filters/orgFacts'
import { formatFilters as formatProductAdFactsFilters } from 'helpers/filters/productAdFacts'
import { formatFilters as formatSBPurchasedProductsFactsFilters } from 'helpers/filters/sbPurchasedProductsFacts'
import { formatFilters as formatScFactsFilters } from 'helpers/filters/sellerCentralFacts'
import { formatFilters as formatSovSearchResultsFilters } from 'helpers/filters/sovSearchResults'
import { formatFilters as formatTargetFactsFilters } from 'helpers/filters/targetFacts'
import { formatFilters as formatVcApiSalesDiagnosticFactsFilters } from 'helpers/filters/vendorCentralApiSalesDiagnosticFacts'
import { formatFilters as formatVcFactsFilters } from 'helpers/filters/vendorCentralFacts'
import { isSegmentDateFilterLongerThanOneYear } from 'helpers/segment'
import { formatDateFromFilter } from 'helpers/utils'
import { transformToString } from 'helpers/widgets'
import { ReactComponent as LineChartWidgetSvg } from 'images/dashboards/line_chart_widget.svg'
import { ReactComponent as MetricWidgetSvg } from 'images/dashboards/metric_widget.svg'
import { ReactComponent as PieChartWidgetSvg } from 'images/dashboards/pie_chart_widget.svg'
import { ReactComponent as TableWidgetSvg } from 'images/dashboards/table_widget.svg'
import { ReactComponent as TextWidgetSvg } from 'images/dashboards/text_widget.svg'
import { PageId } from 'routing/pages'
import { colors } from 'styles/colors'
import {
    CurrencyCode,
    DateRangeFilter,
    Field,
    FiltersState,
    ModifiedWidget,
    UserPermission,
    Widget,
    WidgetFilterMap,
    WidgetType,
} from 'types'
import { DataSources } from 'types/dataSources'

import {
    AMS_GOAL_OPTIONS,
    CAMPAIGN_PLACEMENT_GROUP_BY_PLACEHOLDER_COLUMNS,
    QUERY_GROUP_BY_COLUMNS,
    SPECIAL_AD_GROUP_GROUP_BY_COLUMNS,
    SPECIAL_BRAND_GROUP_BY_COLUMNS,
    SPECIAL_CAMPAIGN_GROUP_BY_COLUMNS,
    SPECIAL_KEYWORD_GROUP_BY_COLUMNS,
    SPECIAL_PORTFOLIO_GROUP_BY_COLUMNS,
    SPECIAL_PRODUCT_AD_GROUP_BY_COLUMNS,
    SPECIAL_TARGET_GROUP_BY_COLUMNS,
} from './dataSources/ams'
import {
    ASIN_USAGE_FACTS_FILTER,
    ASIN_USAGE_GROUP_BY_CONFIG,
    SPECIAL_ASIN_USAGE_GROUP_BY_COLUMNS,
} from './dataSources/asinUsage'
import {
    CATEGORY_PERFORMANCE_FACTS_FILTERS,
    CATEGORY_PERFORMANCE_FACTS_SPECIAL_RENDERER,
} from './dataSources/categoryPerformance'
import { SPECIAL_CHANGELOG_GROUP_BY_COLUMNS } from './dataSources/changelog'
import {
    CATEGORY_INSIGHTS_REPORT_DATE_AGGREGATE_COLUMNS,
    DATE_TIME_AGGREGATE_COLUMNS,
    REPORT_DATE_AGGREGATE_COLUMNS,
    REPORT_DATE_COLUMNS,
    REPORT_DATE_TIME_AGGREGATE_COLUMNS,
    REPORT_DATE_TIME_COLUMNS,
    REPORT_DATE_WEEKLY_GRAIN_COLUMNS,
    SEGMENT_REPORT_DATE_AGGREGATE_COLUMNS,
} from './dataSources/date'
import {
    SPECIAL_DSP_AUDIENCE_GROUP_BY_COLUMNS,
    SPECIAL_DSP_CAMPAIGN_GROUP_BY_COLUMNS,
    SPECIAL_DSP_INVENTORY_GROUP_BY_COLUMNS,
} from './dataSources/dsp'
import {
    MARKET_ANALYSIS_SEGMENTS_GROUP_BY_CONFIG,
    MARKET_ANALYSIS_SEGMENTS_PRODUCT_INFO_DEPENDENCIES,
    MARKET_ANALYSIS_SEGMENTS_PRODUCT_INFO_PLACEHOLDER,
    MARKET_ANALYSIS_SEGMENTS_TIME_AGGREGATE_COLUMNS,
    SPECIAL_MARKET_ANALYSIS_SEGMENTS_GROUP_BY_COLUMNS,
} from './dataSources/marketAnalysisSegments'
import {
    PRODUCT_AD_GOAL_METRICS,
    SALES_AND_TRAFFIC_GOAL_METRICS,
    VC_SALES_AND_TRAFFIC_MANUFACTURING_GOAL_METRICS,
    VC_SALES_AND_TRAFFIC_SOURCING_GOAL_METRICS,
} from './dataSources/metricGoals'
import { ORG_PERFORMANCE_SPECIAL_GROUP_BY_COLUMNS } from './dataSources/orgPerformance'
import { SPECIAL_ORG_FACT_GROUP_BY_COLUMNS } from './dataSources/orgs'
import { SPECIAL_PRODUCT_METADATA_COLUMNS } from './dataSources/productMedatdata'
import { SALES_ESTIMATES_SPECIAL_GROUP_BY_COLUMNS } from './dataSources/salesEstimates'
import {
    SB_PURCHASED_PRODUCTS_GROUP_BY_CONFIG,
    SPECIAL_SB_PURCHASED_PRODUCTS_GROUP_BY_COLUMNS,
} from './dataSources/sbPurchasedProductFacts'
import {
    SEGMENT_FACTS_FILTER,
    SEGMENT_FACTS_GROUP_BY_CONFIG,
    SPECIAL_SEGMENT_FACTS_GROUP_BY_COLUMN_PLACEHOLDERS,
    SPECIAL_SEGMENT_FACTS_GROUP_BY_COLUMNS,
    SPECIAL_SEGMENT_FACTS_METRIC_COLUMNS,
} from './dataSources/segment'
import {
    SPECIAL_SC_GROUP_BY_COLUMN_PLACEHOLDERS,
    SPECIAL_SC_GROUP_BY_COLUMNS,
} from './dataSources/sellerCentral'
import {
    SOV_TIME_AGGREGATE_COLUMNS,
    SPECIAL_SOV_INTRADAY_GROUP_BY_COLUMNS,
    SPECIAL_SOV_KEYWORD_RESEARCH_GROUP_BY_COLUMNS,
} from './dataSources/sov'
import {
    SPECIAL_VC_API_GROUP_BY_COLUMN_PLACEHOLDERS,
    SPECIAL_VC_API_GROUP_BY_COLUMNS,
    SPECIAL_VC_GROUP_BY_COLUMNS,
} from './dataSources/vendorCentral'

const EARLIEST_PERFORMANCE_DATE = '2024-10-27'

const returnsTrue = (): boolean => true

const returnsFalse = (): boolean => false

export interface WidgetTypeEntry {
    name: string
    svgComponent: FunctionComponent<SVGProps<SVGSVGElement>>
}

export const WIDGET_TYPES: Record<WidgetType, WidgetTypeEntry> = {
    [TIME_SERIES]: {
        name: 'Time Series Chart',
        svgComponent: LineChartWidgetSvg,
    },
    [CATEGORY_CHART]: {
        name: 'Categorical Chart',
        svgComponent: PieChartWidgetSvg,
    },
    [TABLE]: {
        name: 'Table',
        svgComponent: TableWidgetSvg,
    },
    [METRIC]: {
        name: 'Metric',
        svgComponent: MetricWidgetSvg,
    },
    [TEXT]: {
        name: 'Custom Text',
        svgComponent: TextWidgetSvg,
    },
}

/**
 * AMS fact fields that should be displayed as options
 * for X-Axis in timeseries charts
 */
export const AMS_TIME_AGGREGATE_FIELDS = [
    'report_date_year',
    'report_date_month',
    'report_date_week',
    REPORT_DATE_DAY_ID,
    REPORT_DATE_HOUR_ID,
]

export const CATEGORY_INSIGHTS_TIME_AGGREGATE_FIELDS = [
    'report_start_date_year',
    'report_start_date_month',
    'report_start_date_week',
]

/**
 * SOV fact fields that should be displayed as options
 * for X-Axis in timeseries charts
 */
export const SOV_TIME_AGGREGATE_FIELDS = [
    'search_time_month',
    'search_time_week',
    'search_time_day',
    'search_time',
]

/**
 * SOV fact fields that should be displayed as options
 * for X-Axis in timeseries charts
 */
export const CHANGELOG_TIME_AGGREGATE_FIELDS = [
    'date_month',
    'date_week',
    'date_day',
    'date',
]

function getPriorPeriodStartDate(
    widget: Pick<
        Widget | ModifiedWidget,
        'period_delta_type' | 'period_delta_date_range'
    >,
    filters: WidgetFilterMap,
    lag?: number
): string | Dayjs | undefined {
    if (
        widget.period_delta_type === 'custom' &&
        widget.period_delta_date_range?.length
    ) {
        return date(widget.period_delta_date_range[0])
    }

    const dates = filters[DATES] as DateRangeFilter | undefined
    if (!dates) {
        return undefined
    }

    const { start, end } = formatDateFromFilter(dates, date(), lag)
    if (!start || !end) {
        return undefined
    }

    const [priorStart] = getPriorPeriodDateRange(
        start,
        end,
        widget.period_delta_type,
        dates
    )
    return priorStart
}

function PriorPeriodWarning({ message }: { message: ReactNode }): ReactElement {
    const { t } = useTranslation()
    return (
        <Tooltip title={message}>
            <Text
                variant="bodySm"
                style={{ color: darken(0.15, colors.warningColor) }}
            >
                <InfoCircleOutlined />{' '}
                {t('dashboards:priorPeriodWarning.label', 'Prior period')}
            </Text>
        </Tooltip>
    )
}

function CategoryPriorPeriodWarning({
    date: earliestDate,
}: {
    date: string
}): ReactElement {
    const { t } = useTranslation()
    return (
        <PriorPeriodWarning
            message={t(
                'dashboards:priorPeriodWarning.categoryDataUnavailable',
                'Category data is not available before {{date}}. Adjust the date range to enable prior period comparison.',
                { date: earliestDate }
            )}
        />
    )
}

function SegmentPriorPeriodWarning(): ReactElement {
    const { t } = useTranslation()
    return (
        <PriorPeriodWarning
            message={t(
                'dashboards:priorPeriodWarning.incompleteData',
                'Prior period has incomplete data. Adjust the date range to less than one year to enable prior period comparison.'
            )}
        />
    )
}

export const DATA_SOURCES: DataSources = {
    '': undefined, // Text widget data source
    campaign_facts: {
        getMetadataKey: () => 'campaign_facts',
        name: widgetsTranslation.campaign_facts.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            FACT_TYPES,
            LABELS,
            BRANDS,
            PORTFOLIOS,
            CAMPAIGNS,
            CAMPAIGN_STATES,
            CAMPAIGN_TARGETING_TYPES,
            CAMPAIGN_AD_FORMATS,
            ADVANCED_AUTOMATIONS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: (widget) => {
            const { group_by } = widget
            const widgetGroupBy = transformToString(group_by)
            return includes(widgetGroupBy, 'placement')
                ? getCampaignPlacementFacts
                : getCampaignFacts
        },
        getExportApiFunc: (widget) => {
            const { group_by } = widget
            const widgetGroupBy = transformToString(group_by)
            return includes(widgetGroupBy, 'placement')
                ? getCampaignPlacementFactsExport
                : getCampaignFactsExport
        },
        formatFiltersFunc: formatCampaignFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_CAMPAIGN_GROUP_BY_COLUMNS,
            ...SPECIAL_BRAND_GROUP_BY_COLUMNS,
            ...SPECIAL_PORTFOLIO_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...CAMPAIGN_PLACEMENT_GROUP_BY_PLACEHOLDER_COLUMNS,
        ],
        groupByConfig: AMS_CHART_FORMATTERS,
        goalOptions: AMS_GOAL_OPTIONS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        defaultFilters: {
            [CAMPAIGN_STATES]: [
                {
                    value: ENABLED,
                    label: CAMPAIGN_STATES_OPTIONS[ENABLED],
                },
            ],
        },
        lag: ADS_LAG_HOURS,
    },
    ad_group_facts: {
        getMetadataKey: () => 'ad_group_facts',
        getApiFunc: () => getAdGroupFacts,
        getExportApiFunc: () => getAdGroupFactsExport,
        formatFiltersFunc: formatAdGroupFactsFilters,
        datePrefix: 'report_date',
        displayed: returnsFalse,
        lag: ADS_LAG_HOURS,
    },
    keyword_facts: {
        getMetadataKey: () => 'keyword_facts',
        name: widgetsTranslation.keyword_facts.name,
        filters: [
            DATES,
            SEARCH_TERM,
            REGIONS,
            COUNTRIES,
            FACT_TYPES,
            LABELS,
            BRANDS,
            PORTFOLIOS,
            CAMPAIGNS,
            CAMPAIGN_STATES,
            CAMPAIGN_TARGETING_TYPES,
            ADVANCED_TEXT,
            KEYWORDS,
            KEYWORD_MATCH_TYPES,
            KEYWORD_STATES,
            AD_GROUP_STATES,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: (widget) => {
            const { filters, group_by } = widget
            const widgetGroupBy = transformToString(group_by)

            return includes(widgetGroupBy, 'query') ||
                !isEmpty(filters[SEARCH_TERM])
                ? getKeywordQueryFacts
                : getKeywordFacts
        },
        getExportApiFunc: (widget) => {
            const { filters, group_by } = widget
            const widgetGroupBy = transformToString(group_by)

            return includes(widgetGroupBy, 'query') ||
                !isEmpty(filters[SEARCH_TERM])
                ? getKeywordQueryFactsExport
                : getKeywordFactsExport
        },
        formatFiltersFunc: formatKeywordFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_KEYWORD_GROUP_BY_COLUMNS,
            ...SPECIAL_AD_GROUP_GROUP_BY_COLUMNS,
            ...SPECIAL_CAMPAIGN_GROUP_BY_COLUMNS,
            ...SPECIAL_BRAND_GROUP_BY_COLUMNS,
            ...SPECIAL_PORTFOLIO_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
            ...QUERY_GROUP_BY_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [...QUERY_GROUP_BY_COLUMNS],
        groupByConfig: AMS_CHART_FORMATTERS,
        goalOptions: AMS_GOAL_OPTIONS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        defaultFilters: {
            [CAMPAIGN_STATES]: [
                {
                    value: ENABLED,
                    label: CAMPAIGN_STATES_OPTIONS[ENABLED],
                },
            ],
            [KEYWORD_STATES]: [
                {
                    value: ENABLED,
                    label: KEYWORD_STATE_LABELS[ENABLED],
                },
            ],
            [AD_GROUP_STATES]: [
                {
                    value: ENABLED,
                    label: STATE_FIELD_OPTIONS[ENABLED],
                },
            ],
        },
        lag: ADS_LAG_HOURS,
    },
    product_ad_facts: {
        getMetadataKey: () => 'product_ad_facts',
        name: widgetsTranslation.product_ad_facts.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            FACT_TYPES,
            LABELS,
            BRANDS,
            PORTFOLIOS,
            CAMPAIGNS,
            CAMPAIGN_STATES,
            CAMPAIGN_TARGETING_TYPES,
            PRODUCT_ADS,
            PRODUCT_ASINS,
            PRODUCT_AD_STATES,
            ADVANCED_PRODUCT_TITLE,
            PRODUCT_NUMBER_OF_ITEMS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getProductAdFacts,
        getExportApiFunc: () => getProductAdFactsExport,
        formatFiltersFunc: formatProductAdFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_AD_GROUP_BY_COLUMNS,
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_AD_GROUP_GROUP_BY_COLUMNS,
            ...SPECIAL_CAMPAIGN_GROUP_BY_COLUMNS,
            ...SPECIAL_BRAND_GROUP_BY_COLUMNS,
            ...SPECIAL_PORTFOLIO_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialMetricColumnRenderers: [...PRODUCT_AD_GOAL_METRICS],
        groupByConfig: AMS_CHART_FORMATTERS,
        goalOptions: AMS_GOAL_OPTIONS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        metricCategoryOrder: [
            'Advertising',
            'Advertising + COGS',
            'COGS',
            'Goal',
        ],
        defaultFilters: {
            [CAMPAIGN_STATES]: [
                {
                    value: ENABLED,
                    label: CAMPAIGN_STATES_OPTIONS[ENABLED],
                },
            ],
            [PRODUCT_AD_STATES]: [
                {
                    value: ENABLED,
                    label: PRODUCT_AD_STATES_OPTIONS[ENABLED],
                },
            ],
        },
        lag: ADS_LAG_HOURS,
    },
    target_facts: {
        getMetadataKey: () => 'target_facts',
        name: widgetsTranslation.target_facts.name,
        filters: [
            DATES,
            SEARCH_TERM,
            REGIONS,
            COUNTRIES,
            FACT_TYPES,
            LABELS,
            BRANDS,
            PORTFOLIOS,
            CAMPAIGNS,
            CAMPAIGN_STATES,
            CAMPAIGN_TARGETING_TYPES,
            TARGET_STATES,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: (widget) => {
            const { group_by, filters } = widget
            const widgetGroupBy = transformToString(group_by)
            return includes(widgetGroupBy, 'query') ||
                !isEmpty(filters[SEARCH_TERM])
                ? getTargetQueryFacts
                : getTargetFacts
        },
        getExportApiFunc: (widget) => {
            const { group_by, filters } = widget
            const widgetGroupBy = transformToString(group_by)
            return includes(widgetGroupBy, 'query') ||
                !isEmpty(filters[SEARCH_TERM])
                ? getTargetQueryFactsExport
                : getTargetFactsExport
        },
        formatFiltersFunc: formatTargetFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_TARGET_GROUP_BY_COLUMNS,
            ...SPECIAL_AD_GROUP_GROUP_BY_COLUMNS,
            ...SPECIAL_CAMPAIGN_GROUP_BY_COLUMNS,
            ...SPECIAL_BRAND_GROUP_BY_COLUMNS,
            ...SPECIAL_PORTFOLIO_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
            ...QUERY_GROUP_BY_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [...QUERY_GROUP_BY_COLUMNS],
        groupByConfig: AMS_CHART_FORMATTERS,
        goalOptions: AMS_GOAL_OPTIONS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        defaultFilters: {
            [TARGET_STATES]: [
                {
                    value: ENABLED,
                    label: TARGET_STATE_LABELS[ENABLED],
                },
            ],
            [CAMPAIGN_STATES]: [
                {
                    value: ENABLED,
                    label: CAMPAIGN_STATES_OPTIONS[ENABLED],
                },
            ],
        },
        lag: ADS_LAG_HOURS,
    },
    sov_search_results: {
        getMetadataKey: () => 'sov',
        name: widgetsTranslation.sov_search_results.name,
        filters: [
            DATES,
            LABELS,
            COUNTRIES,
            SOV_METADATA_BRANDS,
            SOV_BRANDS,
            SOV_KEYWORDS,
            SOV_FOLDS,
            SOV_RANK,
            SOV_RESULT_TYPES,
            SOV_PLATFORMS,
            SOV_COUNTRIES,
            SOV_LANGUAGES,
            SOV_STATES,
            SOV_ASINS,
            ADVANCED_PRODUCT_TITLE,
            PRODUCT_NUMBER_OF_ITEMS,
            SOV_LAYOUT,
            RANGE_LAG,
        ],
        getApiFunc: () => getSovIntradayFactAggregates,
        getExportApiFunc: () => getSovIntradayFactAggregatesExport,
        formatFiltersFunc: formatSovSearchResultsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_SOV_INTRADAY_GROUP_BY_COLUMNS,
        ],
        groupByConfig: SOV_CHART_FORMATTERS,
        datePrefix: 'search_time',
        timeAggregateFields: SOV_TIME_AGGREGATE_COLUMNS,
        displayed: userHasSovReadPermissions,
        metricCategoryOrder: [
            'Shelf Intelligence',
            'Advertising + Shelf Intelligence',
            'Advertising',
        ],
    },
    vc_sales_diagnostic: {
        getMetadataKey: () =>
            'vendor_central_manufacturer_sales_diagnostic_facts',
        name: widgetsTranslation.vc_sales_diagnostic.name,
        filters: [
            DATES,
            COUNTRIES,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            LABELS,
            VC_INTEGRATIONS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcManufacturerFactAggregates,
        getExportApiFunc: () => getVcManufacturerFactAggregatesExport,
        formatFiltersFunc: formatVcFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: VC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasLegacyVendorCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
    },
    vc_source_sales_diagnostic: {
        getMetadataKey: () => 'vendor_central_source_sales_diagnostic_facts',
        name: widgetsTranslation.vc_source_sales_diagnostic.name,
        filters: [
            DATES,
            COUNTRIES,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            LABELS,
            VC_INTEGRATIONS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcSourceFactAggregates,
        getExportApiFunc: () => getVcSourceFactAggregatesExport,
        formatFiltersFunc: formatVcFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: VC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasLegacyVendorCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
    },
    vc_traffic_diagnostic: {
        getMetadataKey: () => 'vendor_central_traffic_diagnostic_facts',
        name: widgetsTranslation.vc_traffic_diagnostic.name,
        filters: [
            DATES,
            COUNTRIES,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            LABELS,
            VC_INTEGRATIONS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcTrafficDiagnosticFactAggregates,
        getExportApiFunc: () => getVcTrafficDiagnosticFactAggregatesExport,
        formatFiltersFunc: formatVcFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: VC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasLegacyVendorCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
    },
    vc_inventory_health: {
        getMetadataKey: () => 'vendor_central_inventory_health_facts',
        name: widgetsTranslation.vc_inventory_health.name,
        filters: [
            DATES,
            COUNTRIES,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            LABELS,
            VC_INTEGRATIONS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcInventoryHealthFactAggregates,
        getExportApiFunc: () => getVcInventoryHealthFactAggregatesExport,
        formatFiltersFunc: formatVcFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: VC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasLegacyVendorCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
    },
    sc_sales_and_traffic: {
        getMetadataKey: () => 'seller_central_sales_and_traffic_facts',
        name: widgetsTranslation.sc_sales_and_traffic.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            AD_ACCOUNT_TYPES,
            SC_ACCOUNTS,
            SC_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            SOV_METADATA_BRANDS,
            RANGE_LAG,
        ],
        getApiFunc: () => getScSalesAndTrafficData,
        getExportApiFunc: () => getScSalesAndTrafficDataExport,
        formatFiltersFunc: formatScFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_SC_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_SC_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        specialMetricColumnRenderers: [...SALES_AND_TRAFFIC_GOAL_METRICS],
        groupByConfig: SC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: SELLER_CENTRAL_LAG_HOURS,
        metricCategoryOrder: [
            'Retail',
            'Goal',
            'Advertising + Retail',
            'Advertising',
        ],
    },
    sc_seller_analytics: {
        getMetadataKey: () => 'seller_central_seller_analytics_facts',
        name: widgetsTranslation.sc_seller_analytics.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            AD_ACCOUNT_TYPES,
            SC_ACCOUNTS,
            SC_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            SOV_METADATA_BRANDS,
            RANGE_LAG,
        ],
        getApiFunc: () => getScSellerAnalyticsData,
        getExportApiFunc: () => getScSellerAnalyticsDataExport,
        formatFiltersFunc: formatScFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_SC_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_SC_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        specialMetricColumnRenderers: [...SALES_AND_TRAFFIC_GOAL_METRICS],
        groupByConfig: SC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: SELLER_CENTRAL_LAG_HOURS,
        metricCategoryOrder: [
            'Retail',
            'Goal',
            'Advertising + Retail',
            'Advertising',
        ],
    },
    sc_all_orders: {
        getMetadataKey: () => 'seller_central_order_facts',
        name: widgetsTranslation.sc_all_orders.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            AD_ACCOUNT_TYPES,
            SC_ACCOUNTS,
            SC_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            SOV_METADATA_BRANDS,
            RANGE_LAG,
        ],
        getApiFunc: () => getScOrderV2Metrics,
        getExportApiFunc: () => getScOrderV2MetricsExport,
        formatFiltersFunc: formatScFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_SC_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_SC_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        groupByConfig: SC_CHART_FORMATTERS,
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: SELLER_CENTRAL_LAG_HOURS,
        metricCategoryOrder: ['Retail', 'Advertising'],
    },
    vc_api_sales_diagnostic: {
        getMetadataKey: () =>
            'vendor_central_selling_partner_api_sales_diagnostic_facts',
        name: widgetsTranslation.vc_api_sales_diagnostic.name,
        filters: [
            DATES,
            COUNTRIES,
            AD_ACCOUNT_TYPES,
            SC_ACCOUNTS,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            SOV_METADATA_BRANDS,
            VC_PROGRAMS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcApiSalesDiagnosticManufacturerMetrics,
        getExportApiFunc: () =>
            getVcApiSalesDiagnosticManufacturerMetricsExport,
        formatFiltersFunc: formatVcApiSalesDiagnosticFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_API_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_VC_API_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        specialMetricColumnRenderers: [
            ...VC_SALES_AND_TRAFFIC_MANUFACTURING_GOAL_METRICS,
        ],
        groupByConfig: SC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
        metricCategoryOrder: [
            'Retail',
            'Goal',
            'Advertising + Retail',
            'Advertising',
        ],
    },
    vc_api_sales_diagnostic_sourcing: {
        getMetadataKey: () =>
            'vendor_central_selling_partner_api_sales_diagnostic_sourcing_facts',
        name: widgetsTranslation.vc_api_sales_diagnostic_sourcing.name,
        filters: [
            DATES,
            COUNTRIES,
            AD_ACCOUNT_TYPES,
            SC_ACCOUNTS,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            SOV_METADATA_BRANDS,
            VC_PROGRAMS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcApiSalesDiagnosticSourcingMetrics,
        getExportApiFunc: () => getVcApiSalesDiagnosticSourcingMetricsExport,
        formatFiltersFunc: formatVcApiSalesDiagnosticFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_API_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_VC_API_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        specialMetricColumnRenderers: [
            ...VC_SALES_AND_TRAFFIC_SOURCING_GOAL_METRICS,
        ],
        groupByConfig: SC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
        metricCategoryOrder: [
            'Retail',
            'Goal',
            'Advertising + Retail',
            'Advertising',
        ],
    },
    vc_api_inventory_health: {
        getMetadataKey: () =>
            'vendor_central_selling_partner_api_inventory_health_facts',
        name: widgetsTranslation.vc_api_inventory_health.name,
        filters: [
            DATES,
            COUNTRIES,
            AD_ACCOUNT_TYPES,
            SC_ACCOUNTS,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            VC_PROGRAMS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcApiInventoryHealthFacts,
        getExportApiFunc: () => getVcApiInventoryHealthFactsExport,
        formatFiltersFunc: formatVcApiSalesDiagnosticFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_API_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_VC_API_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        groupByConfig: SC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
    },
    vc_api_inventory_manufacturing: {
        getMetadataKey: () =>
            'vendor_central_selling_partner_api_inventory_manufacturing_facts',
        name: widgetsTranslation.vc_api_inventory_manufacturing.name,
        filters: [
            DATES,
            COUNTRIES,
            SC_ACCOUNTS,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            SOV_METADATA_BRANDS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcApiInventoryManufacturingMetrics,
        getExportApiFunc: () => getVcApiInventoryManufacturingMetricsExport,
        formatFiltersFunc: formatVcApiSalesDiagnosticFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_API_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_VC_API_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        groupByConfig: SC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
    },
    vc_api_inventory_sourcing: {
        getMetadataKey: () =>
            'vendor_central_selling_partner_api_inventory_sourcing_facts',
        name: widgetsTranslation.vc_api_inventory_sourcing.name,
        filters: [
            DATES,
            COUNTRIES,
            SC_ACCOUNTS,
            PRODUCT_ASINS,
            ADVANCED_PRODUCT_TITLE,
            METRIC_FILTERS,
            LABELS,
            SOV_METADATA_BRANDS,
            RANGE_LAG,
        ],
        getApiFunc: () => getVcApiInventorySourcingMetrics,
        getExportApiFunc: () => getVcApiInventorySourcingMetricsExport,
        formatFiltersFunc: formatVcApiSalesDiagnosticFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_PRODUCT_METADATA_COLUMNS,
            ...SPECIAL_VC_API_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_VC_API_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        groupByDependencies: {
            product_info: [metadataId()],
        },
        groupByConfig: SC_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasSellerCentralReadPermissions,
        lag: VENDOR_CENTRAL_LAG_HOURS,
    },
    dsp_campaign_facts: {
        getMetadataKey: () => 'dsp_campaign_facts',
        name: widgetsTranslation.dsp_campaign_facts.name,
        filters: [
            DATES,
            BRANDS,
            DSP_ADVERTISERS,
            DSP_CREATIVE_NAME,
            DSP_CREATIVE_ID,
            DSP_CREATIVE_TYPE,
            DSP_ORDERS,
            DSP_LINE_ITEMS,
            LABELS,
            RANGE_LAG,
            DSP_COUNTRIES,
            METRIC_FILTERS,
        ],
        getApiFunc: () => getDspCampaignFacts,
        getExportApiFunc: () => getDspCampaignFactsExport,
        formatFiltersFunc: formatDspFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_DSP_CAMPAIGN_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: DSP_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        lag: ADS_LAG_HOURS,
    },
    dsp_inventory_facts: {
        getMetadataKey: () => 'dsp_inventory_facts',
        name: widgetsTranslation.dsp_inventory_facts.name,
        filters: [
            DATES,
            BRANDS,
            DSP_ADVERTISERS,
            DSP_ORDERS,
            DSP_LINE_ITEMS,
            DSP_SUPPLY_SOURCE,
            LABELS,
            RANGE_LAG,
            DSP_COUNTRIES,
            METRIC_FILTERS,
        ],
        getApiFunc: () => getDspInventoryFacts,
        getExportApiFunc: () => getDspInventoryFactsExport,
        formatFiltersFunc: formatDspFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_DSP_INVENTORY_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: DSP_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        lag: ADS_LAG_HOURS,
    },
    dsp_audience_facts: {
        getMetadataKey: () => 'dsp_audience_facts',
        name: widgetsTranslation.dsp_audience_facts.name,
        filters: [
            DATES,
            BRANDS,
            DSP_AUDIENCE_NAME,
            DSP_AUDIENCE_ID,
            DSP_TARGETING_METHOD,
            DSP_ADVERTISERS,
            DSP_ORDERS,
            DSP_LINE_ITEMS,
            LABELS,
            RANGE_LAG,
            DSP_COUNTRIES,
            METRIC_FILTERS,
        ],
        getApiFunc: () => getDspAudienceFacts,
        getExportApiFunc: () => getDspAudienceFactsExport,
        formatFiltersFunc: formatDspFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_DSP_AUDIENCE_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: DSP_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        lag: ADS_LAG_HOURS,
    },
    changelog: {
        getMetadataKey: () => 'changelog',
        name: widgetsTranslation.changelog.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            BRANDS,
            CAMPAIGNS,
            RESOURCE_TYPES,
            AUTOMATIONS,
        ],
        getApiFunc: () => getChangelogSummary,
        getExportApiFunc: () => getChangelogSummaryExport,
        formatFiltersFunc: formatChangelogFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_CHANGELOG_GROUP_BY_COLUMNS,
            ...DATE_TIME_AGGREGATE_COLUMNS,
        ],
        groupByConfig: CHANGELOG_CHART_FORMATTERS,
        datePrefix: 'date',
        timeAggregateFields: DATE_TIME_AGGREGATE_COLUMNS,
        displayed: returnsTrue,
    },
    asin_usage: {
        getMetadataKey: () => 'asin_usage_facts',
        name: widgetsTranslation.asin_usage.name,
        filters: ASIN_USAGE_FACTS_FILTER,
        getApiFunc: () => getAsinUsageSummary,
        getExportApiFunc: () => getAsinUsageSummaryExport,
        formatFiltersFunc: formatAsinUsageFilterParams,
        specialGroupByColumnRenderers: [...SPECIAL_ASIN_USAGE_GROUP_BY_COLUMNS],
        groupByConfig: ASIN_USAGE_GROUP_BY_CONFIG,
        datePrefix: '',
        timeAggregateFields: REPORT_DATE_COLUMNS,
        displayed: userHasCustomerServicePermissions,
    },
    ams_stream_facts: {
        getMetadataKey: () => 'ams_stream_facts',
        name: widgetsTranslation.ams_stream_facts.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            FACT_TYPES,
            LABELS,
            BRANDS,
            PORTFOLIOS,
            CAMPAIGNS,
            CAMPAIGN_STATES,
            CAMPAIGN_TARGETING_TYPES,
            CAMPAIGN_AD_FORMATS,
            ADVANCED_AUTOMATIONS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getAmsStreamFacts,
        getExportApiFunc: () => getAmsStreamFactsExport,
        formatFiltersFunc: formatCampaignFactsFilters,
        specialGroupByColumnRenderers: [
            ...REPORT_DATE_COLUMNS,
            ...REPORT_DATE_TIME_COLUMNS,
            ...SPECIAL_TARGET_GROUP_BY_COLUMNS,
            ...SPECIAL_CAMPAIGN_GROUP_BY_COLUMNS,
            ...SPECIAL_PORTFOLIO_GROUP_BY_COLUMNS,
            ...SPECIAL_KEYWORD_GROUP_BY_COLUMNS,
            ...SPECIAL_BRAND_GROUP_BY_COLUMNS,
        ],
        groupByConfig: {
            ...AMS_CHART_FORMATTERS,
        },
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_TIME_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        defaultFilters: {
            [CAMPAIGN_STATES]: [
                {
                    value: ENABLED,
                    label: CAMPAIGN_STATES_OPTIONS[ENABLED],
                },
            ],
        },
    },
    org_facts: {
        getMetadataKey: () => 'org_facts',
        name: widgetsTranslation.org_facts.name,
        filters: [
            DATES,
            COUNTRIES,
            BRANDS,
            ORGANIZATIONS,
            SC_ACCOUNTS,
            DSP_ADVERTISERS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getOrgFacts,
        getExportApiFunc: () => getOrgFactsExport,
        formatFiltersFunc: formatOrgFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_ORG_FACT_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: ORG_FACT_CHART_FORMATTERS,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: (featurePermissions: UserPermission[]) =>
            userHasAmazonAdvertisingPermissions(featurePermissions) &&
            userHasSellerCentralReadPermissions(featurePermissions),
        metricCategoryOrder: ['Retail', 'Advertising + Retail', 'Advertising'],
    },
    // the `org_performance_facts` datasource is not visible to dashboards and used only in Agents widgets
    org_performance_facts: {
        getMetadataKey: () => 'org_performance_facts',
        getApiFunc: () => getOrgPerformanceFacts,
        formatFiltersFunc: (filters) => filters,
        specialGroupByColumnRenderers: ORG_PERFORMANCE_SPECIAL_GROUP_BY_COLUMNS,
        displayed: returnsFalse,
    },
    // the `sales_estimates` datasource is not visible to dashboards and used only in Agents widgets
    // getApiFunc uses a placeholder — the client does not fetch this datasource directly
    sales_estimates: {
        getMetadataKey: () => 'sales_estimates',
        getApiFunc: () => getOrgPerformanceFacts,
        formatFiltersFunc: (filters) => filters,
        specialGroupByColumnRenderers: SALES_ESTIMATES_SPECIAL_GROUP_BY_COLUMNS,
        displayed: returnsFalse,
    },
    market_analysis_segments: {
        getMetadataKey: () => 'sales_estimates',
        name: widgetsTranslation.market_analysis_segments.name,
        filters: [
            DATES,
            MARKET_ANALYSIS_SEGMENTS,
            SOV_ASINS,
            ADVANCED_PRODUCT_TITLE,
            SEGMENT_BRANDS,
            SELLER_NAME,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: (widget) => (params, options) =>
            getMarketAnalysisSegmentsWidgetData(
                widget as {
                    limit?: number
                    include_change_metrics?: boolean
                    period_delta_type?: 'prior_period' | 'prior_year' | 'custom'
                },
                params,
                options
            ),
        getExportApiFunc: (widget) => (params) =>
            getMarketAnalysisSegmentsExport(
                widget as {
                    limit?: number
                    include_change_metrics?: boolean
                    period_delta_type?: 'prior_period' | 'prior_year' | 'custom'
                },
                params as Parameters<typeof getMarketAnalysisSegmentsExport>[1]
            ),
        formatFiltersFunc: formatMarketAnalysisSegmentsFilters,
        displayed: (_featurePermissions, _featureFlags, gatedFeatures) =>
            !!gatedFeatures?.marketAnalysis,
        datePrefix: 'report_date',
        // Hulk aggregates sales_estimates weekly (Sun–Sat), so a window that
        // doesn't cover whole weeks drops the partial ones at either end.
        dateGrain: { unit: 'week' },
        // Lag back to the second-most-recent Saturday so the latest displayed
        // week is one that has fully closed and been processed. A getter, not a
        // value: computed at module import it went stale in any tab left open
        // across a Saturday. Still whole days × 24 — see
        // hoursToWeeklyDataBoundary.
        get lag() {
            return hoursToWeeklyDataBoundary()
        },
        timeAggregateFields: MARKET_ANALYSIS_SEGMENTS_TIME_AGGREGATE_COLUMNS,
        specialGroupByColumnRenderers:
            SPECIAL_MARKET_ANALYSIS_SEGMENTS_GROUP_BY_COLUMNS,
        specialGroupByColumnPlaceholders: [
            MARKET_ANALYSIS_SEGMENTS_PRODUCT_INFO_PLACEHOLDER,
        ],
        groupByDependencies: {
            product_info: MARKET_ANALYSIS_SEGMENTS_PRODUCT_INFO_DEPENDENCIES,
        },
        groupByConfig: MARKET_ANALYSIS_SEGMENTS_GROUP_BY_CONFIG,
        // Hulk returns sales_estimates values in the marketplace's native
        // currency. Cross-marketplace selection is locked in the filter, so
        // the first segment's marketplace is representative.
        getCurrencyOverride: (_widget, filters) => {
            const segments = filters[MARKET_ANALYSIS_SEGMENTS] as
                | Array<{ marketplace?: string | null }>
                | undefined
            const marketplace = segments?.find(
                (s) => s?.marketplace
            )?.marketplace
            if (!marketplace) {
                return undefined
            }
            const currency =
                COUNTRY_CURRENCIES[
                    marketplace.toUpperCase() as keyof typeof COUNTRY_CURRENCIES
                ]
            return (currency as CurrencyCode | undefined) ?? undefined
        },
        // When no segment is selected (at the widget or tab level) the query
        // returns no rows; guide the user to pick a segment instead of showing
        // the generic "no data" fallback.
        getEmptyStateMessage: (_widget, filters) => {
            if (!isEmpty(filters[MARKET_ANALYSIS_SEGMENTS])) {
                return undefined
            }
            return (
                <Translation>
                    {(t) =>
                        t(
                            'dashboards:widget.dataSources.marketAnalysisSegments.noSegmentSelected',
                            'Select a Market Analysis Segment to see data for this widget.'
                        )
                    }
                </Translation>
            )
        },
    },
    sov_keyword_research: {
        getMetadataKey: () => 'sov_keyword_research',
        getApiFunc: () => getSovKeywordsForAsin,
        formatFiltersFunc: (filters) => filters,
        specialGroupByColumnRenderers:
            SPECIAL_SOV_KEYWORD_RESEARCH_GROUP_BY_COLUMNS,
        displayed: returnsFalse,
    },
    category_insights_facts: {
        getMetadataKey: () => 'category_insights_facts',
        name: widgetsTranslation.category_insights_facts.name,
        filters: [...CATEGORY_PERFORMANCE_FACTS_FILTERS, RANGE_LAG],
        groupByConfig: CATEGORY_PERFORMANCE_FACT_CHART_FORMATTERS,
        specialGroupByColumnRenderers:
            CATEGORY_PERFORMANCE_FACTS_SPECIAL_RENDERER,
        timeAggregateFields: CATEGORY_INSIGHTS_REPORT_DATE_AGGREGATE_COLUMNS,
        getApiFunc: () => getCategoryPerformanceFacts,
        getExportApiFunc: () => getCategoryPerformanceFactsExport,
        formatFiltersFunc: formatCategoryPerformanceFactsFilters,
        displayed: returnsTrue,
        datePrefix: 'report_start_date',
        lag: calculateCategoryPerformanceLag(),
        supportsPriorPeriodComparison: true,
        getPriorPeriodOverride: (widget, filters) => {
            if (!widget.include_change_metrics) {
                return undefined
            }

            const priorPeriodStart = getPriorPeriodStartDate(
                widget,
                filters,
                calculateCategoryPerformanceLag()
            )

            if (
                !priorPeriodStart ||
                date(priorPeriodStart).isSameOrAfter(
                    date(EARLIEST_PERFORMANCE_DATE)
                )
            ) {
                return undefined
            }

            const formattedDate = date(EARLIEST_PERFORMANCE_DATE).format(
                'MMMM D, YYYY'
            )
            return <CategoryPriorPeriodWarning date={formattedDate} />
        },
        defaultFilters: {
            [COUNTRIES]: [
                {
                    value: COUNTRY_CODES.US,
                    label: COUNTRY_LABELS[COUNTRY_CODES.US],
                },
            ],
        },
    },
    segment_facts: {
        getMetadataKey: (widget) => {
            return widget?.data_source_settings?.is_variant_level
                ? 'segment_variant_level_sales_estimate_facts'
                : 'segment_asin_sales_estimate_segmentizer_facts'
        },
        renderCustomWidgetSettings: (values, setValues, setFieldValue) => {
            return (
                <SegmentCustomWidgetSettings
                    values={values}
                    setValues={setValues}
                    setFieldValue={setFieldValue}
                />
            )
        },
        name: widgetsTranslation.segment_facts.name,
        filters: [...SEGMENT_FACTS_FILTER, RANGE_LAG],
        getApiFunc: (widget) => {
            const isVariantLevel = widget.data_source_settings?.is_variant_level
            return isVariantLevel
                ? getSegmentVariantLevelFacts
                : getSegmentFacts
        },
        getExportApiFunc: (widget) => {
            const isVariantLevel = widget.data_source_settings?.is_variant_level
            return isVariantLevel
                ? getSegmentVariantLevelFactsExport
                : getSegmentFactsExport
        },
        getApiParams: (widget) => {
            if (widget?.data_source_settings?.is_variant_level) {
                return {
                    is_parent: false,
                }
            }
            return {}
        },
        formatFiltersFunc: formatSegmentFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_SEGMENT_FACTS_GROUP_BY_COLUMNS,
            ...REPORT_DATE_WEEKLY_GRAIN_COLUMNS,
        ],
        specialGroupByColumnPlaceholders: [
            ...SPECIAL_SEGMENT_FACTS_GROUP_BY_COLUMN_PLACEHOLDERS,
        ],
        specialMetricColumnRenderers: [...SPECIAL_SEGMENT_FACTS_METRIC_COLUMNS],
        getMetricDependencies: (widget): Record<string, Field<any>[]> => {
            const isVariantLevel =
                widget?.data_source_settings?.is_variant_level
            if (isVariantLevel) {
                return {}
            }

            return {
                product_id: [variant_count__total({ excludeLastPeriod: true })],
                product_info: [
                    variant_count__total({ excludeLastPeriod: true }),
                ],
            }
        },
        groupByDependencies: {
            product_info: [productId({ dataIndex: ['product', 'id'] })],
        },
        groupByConfig: SEGMENT_FACTS_GROUP_BY_CONFIG,
        timeAggregateFields: SEGMENT_REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasReadOrManageSegmentPermissions,
        datePrefix: 'report_date',
        lag: calculateSegmentLag(),
        getPriorPeriodOverride: (widget, filters) => {
            if (
                !widget.include_change_metrics ||
                !isSegmentDateFilterLongerThanOneYear(filters)
            ) {
                return undefined
            }

            return <SegmentPriorPeriodWarning />
        },
    },
    sb_purchased_product_facts: {
        getMetadataKey: () => 'sb_purchased_product_facts',
        name: widgetsTranslation.sb_purchased_product_facts.name,
        filters: [
            DATES,
            REGIONS,
            COUNTRIES,
            LABELS,
            BRANDS,
            PORTFOLIOS,
            CAMPAIGNS,
            CAMPAIGN_STATES,
            CAMPAIGN_TARGETING_TYPES,
            PRODUCT_ASINS,
            METRIC_FILTERS,
            RANGE_LAG,
        ],
        getApiFunc: () => getSBPurchasedProductsFacts,
        getExportApiFunc: () => getSBPurchasedProductsFactsExport,
        formatFiltersFunc: formatSBPurchasedProductsFactsFilters,
        specialGroupByColumnRenderers: [
            ...SPECIAL_SB_PURCHASED_PRODUCTS_GROUP_BY_COLUMNS,
            ...SPECIAL_BRAND_GROUP_BY_COLUMNS,
            ...REPORT_DATE_COLUMNS,
        ],
        groupByConfig: SB_PURCHASED_PRODUCTS_GROUP_BY_CONFIG,
        datePrefix: 'report_date',
        timeAggregateFields: REPORT_DATE_AGGREGATE_COLUMNS,
        displayed: userHasAmazonAdvertisingPermissions,
        defaultFilters: {
            [CAMPAIGN_STATES]: [
                {
                    value: ENABLED,
                    label: CAMPAIGN_STATES_OPTIONS[ENABLED],
                },
            ],
        },
        lag: ADS_LAG_HOURS,
    },
}

interface DashboardPageParams {
    [key: string]: string
}

const formatDashboardPageFilters = (
    filters: FiltersState,
    datePrefix = 'report_date',
    hasHourlyData = false,
    rangeLag?: number,
    fiscalYearStartMonth?: number
): DashboardPageParams => {
    const params: DashboardPageParams = {}
    const dates = filters[DATES]
    let { start, end } = formatDateFromFilter(
        dates,
        date(),
        rangeLag,
        fiscalYearStartMonth
    )

    if (!!start && !!end) {
        const dateRangeInDays = date(end).diff(date(start), 'days')
        if (hasHourlyData && dateRangeInDays < MAX_DAYS_RANGE_FOR_HOURLY_DATA) {
            start = `${start}T00:00`
            end = `${end}T23:59`
        }

        params[`${datePrefix}_min`] = start
        params[`${datePrefix}_max`] = end
    }
    return params
}

export const PAGE_DASHBOARD_CONFIG: {
    [k in PageId]?: {
        dataSource?: WidgetDataSourceKey
        formatPageFiltersFunc?:
            | ((filters: FiltersState) => any)
            | ((filters: FiltersState, hasHourlyData?: boolean) => any)
            | ((
                  filters: FiltersState,
                  datePrefix?: string,
                  hasHourlyData?: boolean
              ) => any)
            | ((
                  filters: FiltersState,
                  datePrefix?: string,
                  hasHourlyData?: boolean,
                  rangeLag?: number
              ) => any)
    }
} = {
    keywordPage: {
        dataSource: 'keyword_facts',
        formatPageFiltersFunc: formatKeywordFactsFilters,
    },
    campaignPage: {
        dataSource: 'campaign_facts',
        formatPageFiltersFunc: formatCampaignFactsFilters,
    },
    campaignProductAdPage: {
        dataSource: 'product_ad_facts',
        formatPageFiltersFunc: formatProductAdFactsFilters,
    },
    productPage: {
        dataSource: 'product_ad_facts',
        formatPageFiltersFunc: formatProductAdFactsFilters,
    },
    productDetailPage: {
        formatPageFiltersFunc: formatDashboardPageFilters,
    },
    productAdPage: {
        dataSource: 'product_ad_facts',
        formatPageFiltersFunc: formatProductAdFactsFilters,
    },
    adAccountPage: {
        dataSource: 'campaign_facts',
        formatPageFiltersFunc: formatCampaignFactsFilters,
    },
    homePage: {
        formatPageFiltersFunc: formatDashboardPageFilters,
    },
    dashboardPage: {
        formatPageFiltersFunc: formatDashboardPageFilters,
    },
    dashboardPrintPage: {
        formatPageFiltersFunc: formatDashboardPageFilters,
    },
    portfolioPage: {
        dataSource: 'campaign_facts',
        formatPageFiltersFunc: formatCampaignFactsFilters,
    },
    adGroupPage: {
        dataSource: 'ad_group_facts',
        formatPageFiltersFunc: formatAdGroupFactsFilters,
    },
    dspAdvertiserPage: {
        dataSource: 'dsp_campaign_facts',
        formatPageFiltersFunc: formatDspFactsFilters,
    },
    dspOrderPage: {
        dataSource: 'dsp_campaign_facts',
        formatPageFiltersFunc: formatDspFactsFilters,
    },
    dspLineItemPage: {
        dataSource: 'dsp_campaign_facts',
        formatPageFiltersFunc: formatDspFactsFilters,
    },
    segmentPage: {
        dataSource: 'segment_facts',
        formatPageFiltersFunc: formatSegmentFactsFilters,
    },
    categoryPerformancePage: {
        dataSource: 'category_insights_facts',
        formatPageFiltersFunc: formatCategoryPerformanceFactsFilters,
    },
} as const

export function getWidgetDataSourceKey(
    metadataKey: string,
    widget?: Pick<
        Widget | ModifiedWidget,
        'filters' | 'data_source' | 'group_by' | 'data_source_settings'
    >
): WidgetDataSourceKey | undefined {
    const byMetadataKey = Object.keys(DATA_SOURCES).reduce(
        (acc, k) => {
            const key = k as WidgetDataSourceKey
            const config = DATA_SOURCES[key]
            if (config?.getMetadataKey) {
                acc[config.getMetadataKey(widget)] = key
            }
            return acc
        },
        {} as Record<string, WidgetDataSourceKey>
    )
    return byMetadataKey[metadataKey]
}
